import { type CancelToken } from 'axios';
import {
  useCallback, useEffect, useState,
} from 'react';
import { createCancelToken } from '~/_shared/utils/api/api.helpers';
import { isCancelError } from '~/_shared/utils/api/apiError.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ConstraintsValidationDataMap } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/constraintsValidation/constraints.types';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { type MetricValue } from '../../createBoundaryGroupFromAIModal.types';
import { getConstraintValidationErrorMessages } from './constraints.helpers';
import {
  validateWMSTerritoryConstraints,
  type ValidateWMSTerritoryConstraintsItem,
} from './constraints.repository';

type WMSConstraintsValidationParams = {
  optimizationConstraints: boolean;
  dataMetricsValues: MetricValue[];
  demographicsValues: MetricValue[];
  zipCodesSubset?: ReadonlyArray<string>;
  statesSubset?: ReadonlyArray<string>;
};

export const useWMSConstraintsValidation = ({
  optimizationConstraints,
  demographicsValues,
  dataMetricsValues,
  zipCodesSubset,
  statesSubset,
}: WMSConstraintsValidationParams) => {
  const [validationData, setValidationData] = useState<ConstraintsValidationDataMap>(new Map());
  const [generalError, setGeneralError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [t] = useTranslation();
  const clientId = useClientIdSelector();
  const spreadsheetId = usePrimarySpreadsheetId();
  const mapId = useMapIdSelector();

  const validateConstraints = useCallback(async (cancelToken: CancelToken) => {
    if (!clientId || mapId === null || spreadsheetId === null) {
      return;
    }

    let isAnyConstraintLimited = false;
    const constraints: ValidateWMSTerritoryConstraintsItem[] = [
      ...dataMetricsValues.filter(dataMetricsValue => dataMetricsValue.isConstraint && dataMetricsValue.checked).map(dataMetricsValue => {
        if (dataMetricsValue.constraintLowerBound !== undefined || dataMetricsValue.constraintUpperBound !== undefined) {
          isAnyConstraintLimited = true;
        }
        return {
          metric: dataMetricsValue.id,
          min: dataMetricsValue.constraintLowerBound ?? 1,
          max: dataMetricsValue.constraintUpperBound ?? Number.MAX_SAFE_INTEGER,
        };
      }),
      ...demographicsValues.filter(demographicsValue => demographicsValue.isConstraint && demographicsValue.checked).map(demographicsValue => {
        if (demographicsValue.constraintLowerBound !== undefined || demographicsValue.constraintUpperBound !== undefined) {
          isAnyConstraintLimited = true;
        }
        return {
          metric: demographicsValue.id,
          min: demographicsValue.constraintLowerBound ?? 1,
          max: demographicsValue.constraintUpperBound ?? Number.MAX_SAFE_INTEGER,
        };
      }),
    ];

    if (constraints.length === 0 || !isAnyConstraintLimited) {
      return;
    }

    setIsLoading(true);

    try {
      const validationResult = await validateWMSTerritoryConstraints(clientId, {
        map_id: mapId,
        constraints,
        spreadsheet_id: spreadsheetId,
        dataset_states: statesSubset,
        dataset_zip_codes: zipCodesSubset,
      }, cancelToken);

      setGeneralError('');

      if (validationResult && validationResult.results.length) {
        const newValidationData: ConstraintsValidationDataMap = new Map();

        for (const validationItem of validationResult.results) {
          const errorMessages = getConstraintValidationErrorMessages(validationItem, t);

          newValidationData.set(validationItem.metric, {
            errorMessages,
            highestMin: validationItem.limits.highestMin,
            lowestMax: validationItem.limits.lowestMax,
            max: validationItem.limits.max,
            min: validationItem.limits.min,
          });
        }

        setValidationData(newValidationData);
      }
    }
    catch (err) {
      if (!isCancelError(err)) {
        setValidationData(new Map());
        setGeneralError(t('Something went wrong, please try again.'));
      }
    }
    finally {
      setIsLoading(false);
    }
  }, [clientId, mapId, spreadsheetId, dataMetricsValues, demographicsValues, zipCodesSubset, statesSubset, t]);

  useEffect(() => {
    if (!optimizationConstraints) {
      return;
    }

    const cancelToken = createCancelToken();

    const timeoutId = setTimeout(() => {
      validateConstraints(cancelToken.token);
    }, 750);

    return () => {
      clearTimeout(timeoutId);
      cancelToken.cancel();
    };
  }, [optimizationConstraints, validateConstraints]);

  return {
    areConstraintsValid: !isLoading && (optimizationConstraints
      ? Array.from(validationData.values()).every(validation => validation.errorMessages.length === 0)
      : true),
    generalError,
    isLoading,
    validationData,
  };
};
